'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { cn, getCropImage } from '@pulsex/utils';
const constructUrl = (url, params) => {
    try {
        const parsedUrl = new URL(url);
        const urlParams = new URLSearchParams(parsedUrl.search);
        Object.entries(params)
            .filter(([_, value]) => typeof value !== 'function' && typeof value !== 'object')
            .map(([key, value]) => {
            if (value) {
                urlParams.set(key, String(value));
            }
        });
        // Append new parameters
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                urlParams.set(key, String(value));
            }
        });
        // Construct the final URL with updated query parameters
        parsedUrl.search = urlParams.toString();
        return parsedUrl.toString();
    }
    catch (e) {
        // console.error(e);
        return '';
    }
};
const DEFAULT_CROP_KEY = 'medium';
const getEffectiveCropKey = (config, requestedCropKey) => {
    const cropRatios = config === null || config === void 0 ? void 0 : config.cropRatios;
    const cropSizes = config === null || config === void 0 ? void 0 : config.cropSizes;
    const ratioCode = cropRatios === null || cropRatios === void 0 ? void 0 : cropRatios[requestedCropKey];
    const hasValidRatioCode = typeof ratioCode !== 'undefined';
    const hasValidSize = hasValidRatioCode && typeof (cropSizes === null || cropSizes === void 0 ? void 0 : cropSizes[ratioCode]) === 'number';
    if (hasValidRatioCode && hasValidSize) {
        return requestedCropKey;
    }
    const defaultRatioCode = cropRatios === null || cropRatios === void 0 ? void 0 : cropRatios[DEFAULT_CROP_KEY];
    const hasValidDefault = typeof defaultRatioCode !== 'undefined' &&
        typeof (cropSizes === null || cropSizes === void 0 ? void 0 : cropSizes[defaultRatioCode]) === 'number';
    if (hasValidDefault) {
        if (typeof window !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(`MangoXImage: invalid cropSize "${requestedCropKey}", falling back to "${DEFAULT_CROP_KEY}".`);
        }
        return DEFAULT_CROP_KEY;
    }
    return requestedCropKey;
};
const MangoXImage = ({ config, data, useReelImage = false, className, ImageElementclassName, cropSize, needsMediaURL = false, resolution, style = {}, priority, children, onLoad, enablePictureTag = false, }) => {
    const [primaryError, setPrimaryError] = useState(false);
    const [secondaryError, setSecondaryError] = useState(false);
    const [thirdError, setThirdError] = useState(false);
    // This is because when the image is rendered on the server
    // and it throws an error, the onError event is triggered
    // on the client instantly before the JavaScript is loaded.
    // It's a race condition and most of the time the image
    // stays broken. So we delay the rendering of the actual
    // image to the client, once the JavaScript is loaded.
    const [isOnClient, setIsOnClient] = useState(false);
    useEffect(() => {
        // Set isOnClient to true only when running on the client side
        setIsOnClient(true);
    }, []);
    const upscaleImage = (cropSize, resolution) => {
        const size = {
            width: 300 * (resolution ? resolution : 2),
            height: 300,
            paddingTop: 0,
        };
        const setHeight = (crop) => Math.trunc(size.width / parseFloat(crop));
        const setPaddingTop = (height) => ((height / size.width) * 100).toFixed(2);
        const rawRatio = config['cropSizes'][cropSize];
        const safeRatio = typeof rawRatio === 'number' && isFinite(rawRatio) && rawRatio > 0 ? rawRatio : 1;
        size.height = setHeight(safeRatio);
        size.paddingTop = setPaddingTop(size.height);
        return size;
    };
    const effectiveCropKey = getEffectiveCropKey(config, cropSize);
    let imageSize = upscaleImage(config['cropRatios'][effectiveCropKey], resolution ? resolution : 2);
    const getSrc = ({ format } = {}) => {
        if (!(data === null || data === void 0 ? void 0 : data.image) || thirdError) {
            return `/svgs/fallback/default${config['cropRatios'][effectiveCropKey]}.svg`;
        }
        if (useReelImage) {
            return data === null || data === void 0 ? void 0 : data.image;
        }
        if (primaryError) {
            return constructUrl(data === null || data === void 0 ? void 0 : data.image, {
                width: imageSize.width,
                height: imageSize.height,
                mode: 'crop',
            });
        }
        if (secondaryError) {
            return constructUrl(data === null || data === void 0 ? void 0 : data.image, {
                width: imageSize.width,
                height: imageSize.height,
                mode: 'crop',
                format: 'webp',
            });
        }
        return needsMediaURL
            ? config['MEDIA_URL'] +
                getCropImage(config, (data === null || data === void 0 ? void 0 : data.image) || (data === null || data === void 0 ? void 0 : data.url), effectiveCropKey, imageSize.width)
            : constructUrl(getCropImage(config, (data === null || data === void 0 ? void 0 : data.image) || (data === null || data === void 0 ? void 0 : data.url), effectiveCropKey), {
                width: imageSize.width,
                format: format || 'webp',
            });
    };
    const handleError = () => {
        if (!primaryError) {
            setPrimaryError(true);
        }
        else if (!secondaryError) {
            setSecondaryError(true);
        }
        else if (!thirdError) {
            setThirdError(true);
        }
    };
    const ImageComponent = priority ? Image : 'img';
    // Cap mobile src near viewport width × DPR (~2x)
    const mobileImageWidth = Math.min(imageSize.width, 768);
    return (React.createElement("div", { style: Object.assign({ paddingTop: `${imageSize.paddingTop}%` }, style), className: cn('w-full block rounded-lg bg-neutral-300 overflow-hidden relative bg-gray-200 dark:bg-gray-200', className) },
        !enablePictureTag ? (isOnClient && (React.createElement(ImageComponent, { loading: priority ? 'eager' : 'lazy', alt: (data === null || data === void 0 ? void 0 : data.imageCaption) || (data === null || data === void 0 ? void 0 : data.title) || 'Image', src: getSrc(), onError: handleError, className: cn('absolute left-0 top-0 w-full h-full', ImageElementclassName), width: imageSize.width, height: imageSize.height, priority: priority, onLoad: onLoad }))) : (React.createElement("picture", null,
            React.createElement("source", { media: "(max-width: 768px)", srcSet: constructUrl(getCropImage(config, data.image, effectiveCropKey), {
                    width: mobileImageWidth,
                    format: 'webp',
                }), type: "image/webp" }),
            React.createElement("source", { media: "(min-width: 769px)", srcSet: constructUrl(getCropImage(config, data.image, effectiveCropKey), {
                    width: imageSize.width,
                    format: 'webp',
                }), type: "image/webp" }),
            React.createElement("img", Object.assign({ src: getSrc({ format: 'jpg' }), alt: (data === null || data === void 0 ? void 0 : data.imageCaption) || (data === null || data === void 0 ? void 0 : data.title) || 'Image', width: imageSize.width, height: imageSize.height, loading: priority ? 'eager' : 'lazy' }, (priority ? { fetchpriority: 'high' } : {}), { className: cn('absolute left-0 top-0 w-full h-full', ImageElementclassName), onLoad: onLoad, onError: handleError })))),
        ' ',
        children));
};
export default MangoXImage;
